import { guessSoldAs, type QboLinkEntity, type QboMatching, type QboSwitchMatching, type QboSync } from '@morales/shared';
import { useAuth } from '../auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { ErrorBox, QueryView } from '../components/ui';
import { formatDate, formatMoney, todayIso, whenAt } from '../format';
import { useDesktop } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

const ENTITIES = ['Customer', 'Vendor', 'Item'] as const;
type Unlinked = QboMatching['unlinked'][number];

/**
 * Finance → QuickBooks matching (decision 98, owner/office): which QuickBooks customer, vendor and product is which in
 * the app. The import links exact names on its own; what is left is linked here by a person, or made in the app from
 * the QuickBooks record. The Balances tab sets what each customer owes in QuickBooks beside what the app says.
 */
export function QboMatchingPage() {
  const { t } = useLocale();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'balances' ? 'balances' : 'matching';
  return (
    <div className="desk-page" data-testid="qbo-matching">
      <header className="desk-head">
        <div>
          <h1>{t('qboMatchTitle')}</h1>
          <p className="muted">{t('qboMatchHint')}</p>
        </div>
      </header>
      <div className="seg" role="group" aria-label={t('qboMatchTitle')}>
        <button type="button" aria-pressed={tab === 'matching'} onClick={() => setParams({}, { replace: true })}>
          {t('qboMatchTab')}
        </button>
        <button type="button" aria-pressed={tab === 'balances'} onClick={() => setParams({ tab: 'balances' }, { replace: true })}>
          {t('qboBalancesTab')}
        </button>
      </div>
      {tab === 'matching' ? <Matching /> : <Balances />}
    </div>
  );
}

function NotConnected() {
  const { t } = useLocale();
  return (
    <p className="note" data-testid="qbo-not-connected">
      {t('qboMatchNotConnected')} <Link to="/settings/quickbooks">{t('qboTitle')}</Link>
    </p>
  );
}

const notLinkedOf = (m: QboMatching, e: QboLinkEntity) => {
  const c = m.counts.find((x) => x.entity === e);
  return c ? c.qbo - c.linked : 0;
};

function Matching() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [entity, setEntity] = useState<QboLinkEntity>('Customer');
  const [showLinked, setShowLinked] = useState(false);
  const query = useQuery({ queryKey: qk.qboMatching, queryFn: ({ signal }) => api.qboMatching(signal) });
  const run = useMutation({ mutationFn: () => api.qboMatchRun(), onSuccess: () => void qc.invalidateQueries({ queryKey: ['qbo'] }) });
  return (
    <QueryView query={query}>
      {(m) =>
        !m.connected ? (
          <NotConnected />
        ) : (
          <>
            <CopyFromQuickBooks />
            <SwitchCheck appOnly={m.appOnly} />
            {/* the true totals, not the rows sent (a kind is capped at PER_KIND rows) */}
            <dl className="totals-strip" data-testid="qbo-counts">
              {ENTITIES.map((e) => {
                const c = m.counts.find((x) => x.entity === e) ?? { qbo: 0, linked: 0, appOnly: 0 };
                return (
                  <div key={e} className={c.linked < c.qbo ? 'warn' : undefined}>
                    <dt>{t(`qboEntity_${e}`)}</dt>
                    <dd>{t('qboLinkedOf').replace('{n}', c.linked.toLocaleString(intlLocale)).replace('{of}', c.qbo.toLocaleString(intlLocale))}</dd>
                    <dd className="row-sub">{t('qboAppOnly').replace('{n}', c.appOnly.toLocaleString(intlLocale))}</dd>
                  </div>
                );
              })}
            </dl>
            <div className="row-actions">
              <button type="button" className="btn btn-primary btn-sm" onClick={() => run.mutate()} disabled={run.isPending}>
                {run.isPending ? t('loading') : t('qboMatchRun')}
              </button>
              {run.data ? (
                <span className="muted" role="status">
                  {t('qboMatchRan').replace('{c}', String(run.data.customers)).replace('{v}', String(run.data.vendors)).replace('{i}', String(run.data.items))}
                </span>
              ) : null}
            </div>
            {run.error ? <ErrorBox error={run.error} /> : null}
            <div className="seg" role="group" aria-label={t('qboMatchKind')}>
              {ENTITIES.map((e) => (
                <button key={e} type="button" aria-pressed={entity === e} onClick={() => setEntity(e)}>
                  {t(`qboEntity_${e}`)} ({notLinkedOf(m, e)})
                </button>
              ))}
            </div>
            <div className="seg" role="group" aria-label={t('qboShowWhich')}>
              <button type="button" aria-pressed={!showLinked} onClick={() => setShowLinked(false)}>
                {t('qboNotLinked')}
              </button>
              <button type="button" aria-pressed={showLinked} onClick={() => setShowLinked(true)}>
                {t('qboLinkedList')} ({m.counts.find((c) => c.entity === entity)?.linked ?? 0})
              </button>
            </div>
            {showLinked ? (
              <LinkedTable key={entity} rows={m.linked.filter((l) => l.entity === entity)} />
            ) : (
              <UnlinkedTable key={entity} entity={entity} rows={m.unlinked.filter((u) => u.entity === entity)} appOnly={m.appOnly.filter((a) => a.entity === entity)} />
            )}
          </>
        )
      }
    </QueryView>
  );
}

const SYNC_KINDS = ['linked', 'renamed', 'made', 'restored', 'archived', 'kept', 'filled'] as const;

/**
 * QuickBooks is the list (decision 190, the owner's): the app's customers or products made QuickBooks' own in one step,
 * after a preview of every link, rename, new one and put away. JL: "quickbooks is law".
 */
function CopyFromQuickBooks() {
  const { t } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const [entity, setEntity] = useState<'Customer' | 'Item'>('Customer');
  const [shown, setShown] = useState<QboSync | null>(null);
  const preview = useMutation({ mutationFn: () => api.qboSync(entity, false), onSuccess: setShown });
  const apply = useMutation({
    mutationFn: () => api.qboSync(entity, true),
    onSuccess: (r) => {
      setShown(r);
      void qc.invalidateQueries({ queryKey: ['qbo'] });
      void qc.invalidateQueries({ queryKey: ['customers'] });
      void qc.invalidateQueries({ queryKey: ['items'] });
    },
  });
  if (me?.role !== 'owner') return null;
  const pick = (e: 'Customer' | 'Item') => {
    setEntity(e);
    setShown(null);
    preview.reset();
    apply.reset();
  };
  return (
    <section className="card stack-sm" data-testid="qbo-copy">
      <h2>{t('qboCopyTitle')}</h2>
      <p className="muted">{entity === 'Customer' ? t('qboCopyHintCustomers') : t('qboCopyHintProducts')}</p>
      <p className="muted">{t('qboCopyQuiet')}</p>
      <div className="seg seg-wrap" role="group" aria-label={t('qboCopyTitle')}>
        <button type="button" aria-pressed={entity === 'Customer'} onClick={() => pick('Customer')}>
          {t('qboEntity_Customer')}
        </button>
        <button type="button" aria-pressed={entity === 'Item'} onClick={() => pick('Item')}>
          {t('qboEntity_Item')}
        </button>
      </div>
      <div className="row-actions">
        <button type="button" className="btn btn-secondary btn-sm" disabled={preview.isPending || apply.isPending} onClick={() => preview.mutate()}>
          {preview.isPending ? t('loading') : t('qboCopyPreview')}
        </button>
        {shown && !shown.applied ? (
          <button type="button" className="btn btn-primary btn-sm" disabled={apply.isPending} onClick={() => window.confirm(t('qboCopyConfirm')) && apply.mutate()}>
            {apply.isPending ? t('loading') : t('qboCopyApply')}
          </button>
        ) : null}
      </div>
      {preview.error ? <ErrorBox error={preview.error} /> : null}
      {apply.error ? <ErrorBox error={apply.error} /> : null}
      {shown ? (
        <div className="stack-sm" data-testid="qbo-copy-result">
          <p className={shown.applied ? 'ok-band' : 'note'} role="status">
            {(shown.applied ? t('qboCopyDone') : t('qboCopyWould')).replace('{n}', String(shown.counts.inQbo))}
          </p>
          {entity === 'Customer' && (shown.counts.linked > 0 || shown.counts.made > 0) ? <p className="note">{t('qboCopyMirrorNote')}</p> : null}
          {SYNC_KINDS.map((k) =>
            shown.counts[k] ? (
              <details key={k} open={k === 'kept' || k === 'archived'}>
                <summary>
                  {t(`qboCopy_${k}`)} ({shown.counts[k]})
                </summary>
                <ul className="switch-new-list">
                  {shown[k].map((r) => (
                    <li key={`${r.appId ?? ''}-${r.qboId ?? ''}`}>
                      {r.was ? `${r.was} → ` : ''}
                      {r.name}
                      {syncSaid(r, t) ? <span className="row-sub"> · {syncSaid(r, t)}</span> : null}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null,
          )}
        </div>
      ) : null}
    </section>
  );
}

/** A row of the copy in the screen's language: why, what kept it, the likely twin, what was copied. */
function syncSaid(r: QboSync['kept'][number], t: ReturnType<typeof useLocale>['t']): string {
  const said = [
    r.why ? t(`qboWhy_${r.why}`) : null,
    r.hold ? t(`qboHold_${r.hold}`).replace('{n}', String(r.n ?? '')) : null,
    r.maybe ? t('qboCopyMaybe').replace('{name}', r.maybe) : null,
    r.fields.length ? r.fields.map((f) => t(`qboField_${f}`)).join(', ') : null,
  ];
  return said.filter(Boolean).join(' · ');
}

type SwitchTab = 'customers' | 'products' | 'vendors' | 'new';

/**
 * Matching closed out (plan C10, decision 189): only what the switch needs linked, with the verdict. Linking, making
 * and "Not a product" work in place, the same as in the full lists below.
 */
function SwitchCheck({ appOnly }: { appOnly: QboMatching['appOnly'] }) {
  const { t, intlLocale } = useLocale();
  const desktop = useDesktop();
  const query = useQuery({ queryKey: qk.qboSwitchMatching, queryFn: ({ signal }) => api.qboSwitchMatching(signal) });
  const [tab, setTab] = useState<SwitchTab | null>(null);
  const s = query.data;
  if (!s) return query.error ? <ErrorBox error={query.error} /> : null;
  if (!s.connected) return null;
  const open: SwitchTab = tab ?? (s.counts.customers ? 'customers' : s.counts.products ? 'products' : 'new');
  const day = (d: string | null) => (d ? formatDate(d, intlLocale, 'date') : '—');
  const capped = (n: number, shown: number) => (n > shown ? <p className="note">{t('switchMatchCapped').replace('{n}', String(shown))}</p> : null);
  const rowsOf = (entity: QboLinkEntity, list: { qboId: string; name: string; balance?: number | null; detail: string }[]) => {
    const listId = `switch-app-${entity}`;
    const pick = appOnly.filter((a) => a.entity === entity);
    const rows = list.map((r) => (
      <UnlinkedRow key={r.qboId} row={{ entity, qboId: r.qboId, name: r.name, active: true, balance: r.balance ?? null }} listId={listId} appOnly={pick} card={!desktop} detail={r.detail} ignorable={entity === 'Item'} />
    ));
    if (!list.length) return <p className="muted">{t('switchMatchNone')}</p>;
    return (
      <>
        <datalist id={listId}>
          {pick.map((a) => (
            <option key={a.id} value={a.name} label={a.archived ? `${a.name} (${t('qboArchived')})` : undefined} />
          ))}
        </datalist>
        {desktop ? (
          <table className="data-table" data-testid={`switch-${entity}`}>
            <thead>
              <tr>
                <th>{t('qboInQuickBooksCol')}</th>
                {entity === 'Customer' ? <th className="al-right">{t('qboBalanceCol')}</th> : null}
                <th>{t('qboAppRecordCol')}</th>
                <th />
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </table>
        ) : (
          <ul className="stack-sm plain-list" data-testid={`switch-${entity}`}>
            {rows}
          </ul>
        )}
      </>
    );
  };
  return (
    <section className={`card stack-sm switch-check${s.ready ? '' : ' not-ready'}`} data-testid="switch-check">
      <h2>{t('switchMatchTitle')}</h2>
      <p className={s.ready ? 'ok-band' : 'warn-band'} role="status" data-testid="switch-verdict">
        {s.ready ? t('switchMatchReady') : t('switchMatchNotReady').replace('{c}', String(s.counts.customers)).replace('{p}', String(s.counts.products))}
      </p>
      <p className="muted">{t('switchMatchHint').replace('{since}', day(s.since))}</p>
      <div className="seg seg-wrap" role="group" aria-label={t('switchMatchTitle')}>
        <button type="button" aria-pressed={open === 'customers'} onClick={() => setTab('customers')}>
          {t('switchMatchCustomers')} ({s.counts.customers})
        </button>
        <button type="button" aria-pressed={open === 'products'} onClick={() => setTab('products')}>
          {t('switchMatchProducts')} ({s.counts.products})
        </button>
        <button type="button" aria-pressed={open === 'vendors'} onClick={() => setTab('vendors')}>
          {t('switchMatchVendors')} ({s.counts.vendors})
        </button>
        <button type="button" aria-pressed={open === 'new'} onClick={() => setTab('new')}>
          {t('switchMatchNew')} ({s.counts.appCustomers + s.counts.appProducts})
        </button>
      </div>
      {open === 'customers' ? (
        <>
          {rowsOf('Customer', s.customers.map((c) => ({ ...c, detail: c.transactions ? t('switchMatchCustomerDetail').replace('{n}', String(c.transactions)).replace('{last}', day(c.lastActivity)) : t('switchMatchOwesOnly') })))}
          {capped(s.counts.customers, s.customers.length)}
        </>
      ) : open === 'products' ? (
        <>
          {rowsOf('Item', s.products.map((p) => ({ ...p, detail: t('switchMatchProductDetail').replace('{amount}', formatMoney(p.sold, intlLocale)).replace('{n}', String(p.lines)).replace('{last}', day(p.lastSold)) })))}
          {capped(s.counts.products, s.products.length)}
        </>
      ) : open === 'vendors' ? (
        <>
          <p className="note">{t('switchMatchVendorsHint')}</p>
          {rowsOf('Vendor', s.vendors.map((v) => ({ ...v, detail: t('switchMatchVendorDetail').replace('{n}', String(v.transactions)).replace('{last}', day(v.lastActivity)) })))}
          {capped(s.counts.vendors, s.vendors.length)}
        </>
      ) : (
        <NewInQuickBooks s={s} />
      )}
    </section>
  );
}

type AppRow = QboSwitchMatching['appCustomers'][number];

function NewInQuickBooks({ s }: { s: QboSwitchMatching }) {
  const { t } = useLocale();
  const list = (title: string, rows: AppRow[], href: (id: string) => string, total: number) => {
    // QuickBooks would refuse these on their first invoice: said first, with why, so they are linked before the switch
    const refused = rows.filter((r) => r.why !== 'new');
    const made = rows.filter((r) => r.why === 'new');
    return (
      <div className="stack-sm">
        <h3>
          {title} ({total})
        </h3>
        {refused.length ? (
          <ul className="stack-sm plain-list" data-testid="switch-refused">
            {refused.map((r) => (
              <li key={r.id} className="warn-band">
                <Link to={href(r.id)}>{r.name}</Link> · {t(`switchMatchWhy_${r.why}`)}
              </li>
            ))}
          </ul>
        ) : null}
        {made.length ? (
          <ul className="switch-new-list">
            {made.map((r) => (
              <li key={r.id}>
                <Link to={href(r.id)}>{r.name}</Link>
              </li>
            ))}
          </ul>
        ) : null}
        {!rows.length ? <p className="muted">{t('switchMatchNone')}</p> : null}
        {total > rows.length ? <p className="note">{t('switchMatchCapped').replace('{n}', String(rows.length))}</p> : null}
      </div>
    );
  };
  return (
    <div className="stack-sm" data-testid="switch-new">
      <p className="note">{t('switchMatchNewHint')}</p>
      {list(t('switchMatchNewCustomers'), s.appCustomers, (id) => `/customers/${id}`, s.counts.appCustomers)}
      {list(t('switchMatchNewProducts'), s.appProducts, (id) => `/items/${id}`, s.counts.appProducts)}
    </div>
  );
}

const PAGE = 50;
/** the server sends at most this many rows of a kind (PER_KIND in routes/qbo-links.ts) */
const CAP = 2000;

function UnlinkedTable({ entity, rows: all, appOnly }: { entity: QboLinkEntity; rows: Unlinked[]; appOnly: QboMatching['appOnly'] }) {
  const { t } = useLocale();
  const desktop = useDesktop();
  const [find, setFind] = useState('');
  const [shown, setShown] = useState(PAGE);
  if (!all.length) return <p className="ok-band">{t('qboAllLinked')}</p>;
  const words = find.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matching = words.length ? all.filter((r) => words.every((w) => r.name.toLowerCase().includes(w))) : all;
  const rows = matching.slice(0, shown);
  const listId = `qbo-app-${entity}`;
  const capped = all.length >= CAP;
  return (
    <div className={desktop ? 'table-wrap' : 'stack-sm'}>
      <label className="field">
        <span>{t('qboFind')}</span>
        <input type="search" value={find} onChange={(e) => setFind(e.target.value)} placeholder={t('qboFindPlaceholder')} />
      </label>
      {capped ? <p className="note">{t('qboCapped').replace('{n}', String(CAP))}</p> : null}
      <datalist id={listId}>
        {appOnly.map((a) => (
          <option key={a.id} value={a.name} label={a.archived ? `${a.name} (${t('qboArchived')})` : undefined} />
        ))}
      </datalist>
      {desktop ? (
        <table className="data-table" data-testid="qbo-unlinked">
          <caption>{t('qboUnlinkedCaption')}</caption>
          <thead>
            <tr>
              <th>{t('qboInQuickBooksCol')}</th>
              {entity === 'Customer' ? <th className="al-right">{t('qboBalanceCol')}</th> : null}
              <th>{t('qboAppRecordCol')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <UnlinkedRow key={r.qboId} row={r} listId={listId} appOnly={appOnly} card={false} />
            ))}
          </tbody>
        </table>
      ) : (
        <ul className="stack-sm plain-list" data-testid="qbo-unlinked" aria-label={t('qboUnlinkedCaption')}>
          {rows.map((r) => (
            <UnlinkedRow key={r.qboId} row={r} listId={listId} appOnly={appOnly} card />
          ))}
        </ul>
      )}
      {matching.length > rows.length ? (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShown(shown + PAGE)}>
          {t('qboShowMore').replace('{n}', String(rows.length)).replace('{of}', String(matching.length))}
        </button>
      ) : null}
    </div>
  );
}

type SoldAs = QboMatching['linked'][number]['unit'];

/** "Sold as": what one QuickBooks quantity is: the product's box, or pounds/each/dozens/bunches out of it (decision 166). */
function SoldAsSelect({ value, onChange, label, disabled }: { value: SoldAs; onChange: (u: SoldAs) => void; label: string; disabled?: boolean }) {
  const { t } = useLocale();
  return (
    <select value={value ?? ''} onChange={(e) => onChange((e.target.value || null) as SoldAs)} aria-label={label} title={t('qboSoldAsHint')} disabled={disabled} style={{ minWidth: 92, width: 'auto' }}>
      <option value="">{t('qboSoldAsBox')}</option>
      <option value="LB">{t('qboSoldAsLb')}</option>
      <option value="EA">{t('qboSoldAsEa')}</option>
      <option value="DZ">{t('qboSoldAsDz')}</option>
      <option value="BN">{t('qboSoldAsBn')}</option>
    </select>
  );
}

function UnlinkedRow({ row, listId, appOnly, card, detail, ignorable }: { row: Unlinked; listId: string; appOnly: QboMatching['appOnly']; card: boolean; detail?: string; ignorable?: boolean }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [typed, setTyped] = useState('');
  const fold = (s: string) => s.trim().toLowerCase();
  const pick = appOnly.find((a) => fold(a.name) === fold(typed));
  const done = () => void qc.invalidateQueries({ queryKey: ['qbo'] });
  const [unit, setUnit] = useState<SoldAs>(row.entity === 'Item' ? guessSoldAs(row.name) : null);
  const link = useMutation({ mutationFn: (appId: string) => api.qboLink({ entity: row.entity, qboId: row.qboId, appId, ...(unit ? { unit } : {}) }), onSuccess: done });
  const make = useMutation({ mutationFn: () => api.qboCreateFrom({ entity: row.entity, qboId: row.qboId }), onSuccess: done });
  // a fee, freight or a deposit sold as a QuickBooks product is not asked about again (decision 189)
  const ignore = useMutation({ mutationFn: () => api.qboNotAProduct(row.qboId), onSuccess: done });
  const busy = link.isPending || make.isPending || ignore.isPending;
  const name = (
    <>
      {row.name}
      {row.active === false ? <span className="row-sub"> · {t('qboInactive')}</span> : null}
      {detail ? <span className="row-sub"> · {detail}</span> : null}
      {link.error ? <ErrorBox error={link.error} /> : null}
      {make.error ? <ErrorBox error={make.error} /> : null}
      {ignore.error ? <ErrorBox error={ignore.error} /> : null}
    </>
  );
  const balance = row.entity === 'Customer' && row.balance ? formatMoney(row.balance, intlLocale) : '';
  const input = <input type="text" list={listId} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={t('qboPickApp')} aria-label={`${t('qboAppRecordCol')}: ${row.name}`} aria-invalid={(typed.trim() !== '' && !pick) || undefined} />;
  const soldAs = row.entity === 'Item' ? <SoldAsSelect value={unit} onChange={setUnit} label={`${t('qboSoldAs')}: ${row.name}`} disabled={busy} /> : null;
  const actions = (
    <>
      {soldAs}
      <button type="button" className="btn btn-primary btn-sm" disabled={!pick || busy} onClick={() => pick && link.mutate(pick.id)}>
        {t('qboLink')}
      </button>
      {row.entity !== 'Item' ? (
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} title={t('qboMakeHint')} onClick={() => make.mutate()}>
          {t('qboMake')}
        </button>
      ) : ignorable ? (
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => window.confirm(t('notAProductConfirm').replace('{name}', row.name)) && ignore.mutate()}>
          {t('notAProduct')}
        </button>
      ) : null}
    </>
  );
  if (card)
    return (
      <li className={`card stack-sm${row.active === false ? ' muted' : ''}`}>
        <div>
          <strong>{name}</strong>
          {balance ? <span className="row-sub"> · {t('qboBalanceCol')} {balance}</span> : null}
        </div>
        {input}
        <div className="row-actions">{actions}</div>
      </li>
    );
  return (
    <tr className={row.active === false ? 'muted' : undefined}>
      <td>{name}</td>
      {row.entity === 'Customer' ? <td className="al-right">{balance}</td> : null}
      <td>{input}</td>
      <td className="row-actions">{actions}</td>
    </tr>
  );
}

const HOW = { key: 'qboHow_key', name: 'qboHow_name', alias: 'qboHow_alias', manual: 'qboHow_manual', created: 'qboHow_created', pushed: 'qboHow_pushed' } as const;

function LinkedTable({ rows: all }: { rows: QboMatching['linked'] }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [find, setFind] = useState('');
  const [shown, setShown] = useState(PAGE);
  const unlink = useMutation({ mutationFn: (r: QboMatching['linked'][number]) => api.qboUnlink({ entity: r.entity, qboId: r.qboId }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['qbo'] }) });
  // a product's unit changed: its sales read again in it, and the stock they took is taken again in boxes (decision 166)
  const retune = useMutation({ mutationFn: (v: { r: QboMatching['linked'][number]; unit: SoldAs }) => api.qboLink({ entity: v.r.entity, qboId: v.r.qboId, appId: v.r.appId, unit: v.unit }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['qbo'] }) });
  if (!all.length) return <p className="muted">{t('qboNoneLinked')}</p>;
  const items = all[0]?.entity === 'Item';
  const words = find.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matching = words.length ? all.filter((r) => words.every((w) => `${r.qboName} ${r.appName}`.toLowerCase().includes(w))) : all;
  const rows = matching.slice(0, shown);
  return (
    <div className="table-wrap">
      <label className="field">
        <span>{t('qboFind')}</span>
        <input type="search" value={find} onChange={(e) => setFind(e.target.value)} placeholder={t('qboFindEither')} />
      </label>
      {all.length >= CAP ? <p className="note">{t('qboCapped').replace('{n}', String(CAP))}</p> : null}
      {unlink.error ? <ErrorBox error={unlink.error} /> : null}
      {retune.error ? <ErrorBox error={retune.error} /> : null}
      <table className="data-table" data-testid="qbo-linked">
        <caption>{t('qboLinkedList')}</caption>
        <thead>
          <tr>
            <th>{t('qboInQuickBooksCol')}</th>
            <th>{t('qboAppRecordCol')}</th>
            {items ? <th>{t('qboSoldAs')}</th> : null}
            <th>{t('qboHowCol')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.qboId}>
              <td>{r.qboName}</td>
              <td>{r.appName}</td>
              {items ? (
                <td>
                  <SoldAsSelect
                    value={r.unit}
                    label={`${t('qboSoldAs')}: ${r.qboName}`}
                    disabled={retune.isPending}
                    onChange={(unit) => {
                      if (window.confirm(t('qboSoldAsConfirm').replace('{name}', r.qboName))) retune.mutate({ r, unit });
                    }}
                  />
                </td>
              ) : null}
              <td className="row-sub">
                {t(HOW[r.how])}
                {r.linkedBy ? ` · ${r.linkedBy}` : ''} · {formatDate(todayIso(new Date(r.linkedAt)), intlLocale, 'date')}
              </td>
              <td>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={unlink.isPending}
                  onClick={() => {
                    if (window.confirm(t('qboUnlinkConfirm').replace('{name}', r.qboName))) unlink.mutate(r);
                  }}
                >
                  {t('qboUnlink')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {matching.length > rows.length ? (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShown(shown + PAGE)}>
          {t('qboShowMore').replace('{n}', String(rows.length)).replace('{of}', String(matching.length))}
        </button>
      ) : null}
    </div>
  );
}

function Balances() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  // the last import's balances, or QuickBooks' own A/R aging report read now (plan C11)
  const [source, setSource] = useState<'import' | 'report'>('import');
  // the live report asks QuickBooks: read when asked, not on every return to the window
  const live = source === 'report';
  const query = useQuery({ queryKey: [...qk.qboBalances, source], queryFn: ({ signal }) => api.qboBalances(signal, source), ...(live ? { retry: false, staleTime: 5 * 60_000, refetchOnWindowFocus: false } : {}) });
  const unlink = useMutation({ mutationFn: (qboId: string) => api.qboUnlink({ entity: 'Customer', qboId }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['qbo'] }) });
  const money = (v: number) => formatMoney(v, intlLocale);
  return (
    <>
      {/* outside the query's view, so an error from QuickBooks never leaves the person stuck on the report */}
      <div className="seg" role="group" aria-label={t('qboBalancesSource')} data-testid="qbo-balance-source">
        <button type="button" aria-pressed={source === 'import'} onClick={() => setSource('import')}>
          {t('qboBalancesFromImport')}
        </button>
        <button type="button" aria-pressed={source === 'report'} onClick={() => setSource('report')}>
          {t('qboBalancesFromReport')}
        </button>
      </div>
      <QueryView query={query}>
      {(b) =>
        !b.connected ? (
          <NotConnected />
        ) : (
          <>
            {b.source === 'report' ? (
              (() => {
                // in cents: what the report holds that the rows below do not explain
                const off = b.reportTotal === null ? 0 : Math.round(b.reportTotal * 100) - Math.round(b.totals.qbo * 100) - Math.round(b.totals.qboUnlinked * 100);
                return (
                  <p className={off !== 0 ? 'warn-band' : 'muted'} data-testid="qbo-report-total">
                    {t('qboReportTotal').replace('{total}', money(b.reportTotal ?? 0)).replace('{when}', whenAt(b.asOf ?? new Date().toISOString(), intlLocale))}
                    {off !== 0 ? ` ${t('qboReportOff').replace('{amount}', money(off / 100))}` : null}
                  </p>
                );
              })()
            ) : b.asOf ? (
              <p className="muted">{t('qboBalancesAsOf').replace('{when}', whenAt(b.asOf, intlLocale))}</p>
            ) : null}
            {!b.ledgerFrom ? <p className="note">{t('qboBalancesNoLedger')}</p> : <p className="muted">{t('qboBalancesHint').replace('{date}', formatDate(b.ledgerFrom, intlLocale, 'date'))}</p>}
            <dl className="totals-strip" data-testid="qbo-balance-totals">
              <div>
                <dt>{t('qboOwedInQuickBooks')}</dt>
                <dd>{money(b.totals.qbo)}</dd>
              </div>
              <div>
                <dt>{t('qboOwedInApp')}</dt>
                <dd>{b.totals.app === null ? '—' : money(b.totals.app)}</dd>
              </div>
              <div>
                <dt>{t('qboOutsideLinks')}</dt>
                <dd className="row-sub">
                  {t('qboTitle')} {money(b.totals.qboUnlinked)} · {t('qboAppCol')} {b.totals.appUnlinked === null ? '—' : money(b.totals.appUnlinked)}
                </dd>
              </div>
              <div className={b.totals.differ ? 'warn' : undefined}>
                <dt>{t('qboDiffer')}</dt>
                <dd>
                  {b.totals.differ} / {b.totals.customers}
                </dd>
              </div>
            </dl>
            {unlink.error ? <ErrorBox error={unlink.error} /> : null}
            {b.rows.length ? (
              <div className="table-wrap">
                <table className="data-table" data-testid="qbo-balance-rows">
                  <caption>{t('qboBalancesCaption')}</caption>
                  <thead>
                    <tr>
                      <th>{t('customer')}</th>
                      <th className="al-right">{t('qboTitle')}</th>
                      <th className="al-right">{t('qboAppCol')}</th>
                      <th className="al-right">{t('qboDiffCol')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((r) => (
                      <tr key={r.qboId}>
                        <td>
                          <Link to={`/customers/${r.customerId}/statement`}>{r.customerName}</Link>
                        </td>
                        <td className="al-right">{money(r.qboBalance)}</td>
                        <td className="al-right">{r.appBalance === null ? '—' : money(r.appBalance)}</td>
                        <td className={`al-right${r.diff && Math.abs(r.diff) >= 0.01 ? ' bad' : ''}`}>{r.diff === null ? '—' : Math.abs(r.diff) < 0.01 ? '✓' : money(r.diff)}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={unlink.isPending}
                            onClick={() => {
                              if (window.confirm(t('qboUnlinkConfirm').replace('{name}', r.customerName))) unlink.mutate(r.qboId);
                            }}
                          >
                            {t('qboUnlink')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="muted">{t('qboBalancesNone')}</p>
            )}
            {b.unlinkedOwing.length ? (
              <div className="table-wrap">
                <table className="data-table" data-testid="qbo-unlinked-owing">
                  <caption>{t('qboUnlinkedOwing')}</caption>
                  <thead>
                    <tr>
                      <th>{t('qboInQuickBooksCol')}</th>
                      <th className="al-right">{t('qboBalanceCol')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {b.unlinkedOwing.map((r) => (
                      <tr key={r.qboId}>
                        <td>{r.name}</td>
                        <td className="al-right">{money(r.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        )
      }
      </QueryView>
    </>
  );
}
